import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Lock, X } from "lucide-react";
import Navbar from "../components/Navbar";
import { formatPrice, getProductById } from "../config/pricing";

const productLabel = (order) => {
  const snap = order?.product_snapshot;
  if (snap?.title) return snap.title;
  const live = getProductById(order?.selected_product_id);
  return live?.title || order?.selected_product_id || "—";
};
const validityLabel = (order) =>
  order?.validity_days ? `${order.validity_days}-day validity` : "—";
const summaryLine = (order) => {
  const snap = order?.product_snapshot || getProductById(order?.selected_product_id) || {};
  const lbl = snap.summaryLabel || snap.title || order?.selected_product_id || "Reservation";
  const v = order?.validity_days ?? snap.validityDays;
  return `${lbl} · ${v}-day validity · ${formatPrice(order?.amount ?? snap.price ?? 0)}`;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gd_admin_token";

const STATUS_BADGES = {
  pending: { label: "Payment pending", cls: "gd-status-yellow" },
  paid: { label: "Paid", cls: "gd-status-green" },
  in_fulfilment: { label: "In fulfilment", cls: "gd-status-blue" },
  pnr_generated: { label: "PNR generated", cls: "gd-status-purple" },
  sent: { label: "Sent", cls: "gd-status-darkgreen" },
  reissue_requested: { label: "Reissue requested", cls: "gd-status-amber" },
  refunded: { label: "Refunded", cls: "gd-status-red" },
  failed: { label: "Failed", cls: "gd-status-red" },
};

function StatusBadge({ status }) {
  const def = STATUS_BADGES[status] || { label: status || "—", cls: "gd-status-gray" };
  return <span className={`gd-status ${def.cls}`}>{def.label}</span>;
}

function Login({ onAuth }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuth(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      <Navbar variant="admin" />
      <main
        className="gd-page"
        style={{
          padding: "80px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <form
          onSubmit={submit}
          className="gd-card gd-fade-up"
          style={{ padding: 32, width: "100%", maxWidth: 400 }}
          data-testid="admin-login-form"
        >
          <div
            style={{
              width: 44, height: 44, borderRadius: 10,
              background: "var(--primary-soft)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              marginBottom: 18,
            }}
          >
            <Lock size={18} color="var(--primary)" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: 0, marginBottom: 6 }}>
            Operations access
          </h1>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0, marginBottom: 22 }}>
            Enter admin password to continue.
          </p>

          <label className="gd-label">Password</label>
          <input
            type="password"
            className="gd-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            data-testid="admin-password-input"
          />

          {error && (
            <div
              style={{
                marginTop: 12,
                padding: "8px 12px",
                background: "var(--danger-soft)",
                color: "#991b1b",
                borderRadius: 8,
                fontSize: 13,
              }}
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="gd-btn-primary"
            style={{ marginTop: 18 }}
            disabled={loading || !password}
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 size={16} /> : "Sign in"}
          </button>
        </form>
      </main>
    </div>
  );
}

function FulfilModal({ order, onClose, token, onUpdated }) {
  const [pnr, setPnr] = useState(order.pnr || "");
  const [airline, setAirline] = useState(order.airline || "");
  const [validUntil, setValidUntil] = useState(order.valid_until || "");
  const [pdfUrl, setPdfUrl] = useState(order.pdf_url || "");
  const [deliveryStatus, setDeliveryStatus] = useState("whatsapp");
  const [notes, setNotes] = useState(order.notes || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const save = async (action) => {
    setBusy(true);
    setMsg("");
    try {
      let url = `${API}/admin/orders/${order.id}`;
      let payload = {
        pnr: pnr || null,
        airline: airline || null,
        valid_until: validUntil || null,
        pdf_url: pdfUrl || null,
        delivery_status: deliveryStatus,
        notes: notes || null,
      };

      if (action === "send") {
        url = `${API}/admin/orders/${order.id}/send`;
      } else if (action === "refund") {
        url = `${API}/admin/orders/${order.id}/refund`;
        payload = {};
      } else if (action === "reissue") {
        url = `${API}/admin/orders/${order.id}/reissue`;
        payload = {};
      } else if (action === "pnr_generated") {
        payload.fulfilment_status = "pnr_generated";
      }

      const method = action === "refund" || action === "reissue" || action === "send" ? "post" : "patch";
      const { data } = await axios[method](url, payload, auth);
      onUpdated(data);
      if (action === "send") onClose();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const handlePdf = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // No real upload — store filename for reference (UI only)
    setPdfUrl(file.name);
  };

  return (
    <div className="gd-modal-overlay" onClick={onClose}>
      <div className="gd-modal" onClick={(e) => e.stopPropagation()} data-testid="fulfil-modal">
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>
              Fulfil order
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>{order.order_code}</div>
          </div>
          <button
            onClick={onClose}
            className="gd-btn-ghost"
            style={{ padding: 8 }}
            data-testid="fulfil-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {/* Order summary header */}
          <div
            style={{
              padding: "10px 14px",
              border: "1px solid var(--border)",
              borderRadius: 10,
              background: "var(--surface)",
              fontSize: 13,
              color: "var(--text-muted)",
              marginBottom: 18,
              lineHeight: 1.5,
            }}
            data-testid="fulfil-order-summary"
          >
            <strong style={{ color: "var(--text)" }}>
              {summaryLine(order)} · ₹{(order.amount || 0).toLocaleString("en-IN")}
            </strong>
            <div>
              {order.full_name} · {order.email} · +91 {order.whatsapp}
            </div>
            {(order.flight_origin || order.hotel_city || order.hotel_cities) && (
              <div>
                {order.flight_origin && order.flight_destination
                  ? `${order.flight_origin} → ${order.flight_destination}`
                  : order.hotel_cities || order.hotel_city}
                {order.departure_date ? ` · ${order.departure_date}` : ""}
                {order.return_date ? ` → ${order.return_date}` : ""}
                {order.check_in_date ? ` · ${order.check_in_date}` : ""}
                {order.check_out_date ? ` → ${order.check_out_date}` : ""}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label className="gd-label">Upload PDF</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePdf}
                className="gd-input"
                style={{ padding: 8 }}
                data-testid="fulfil-pdf"
              />
              {pdfUrl && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>{pdfUrl}</div>}
            </div>
            <div>
              <label className="gd-label">PNR / booking ref</label>
              <input
                className="gd-input"
                value={pnr}
                onChange={(e) => setPnr(e.target.value.toUpperCase())}
                placeholder="ABCDEF"
                data-testid="fulfil-pnr"
              />
            </div>
            <div>
              <label className="gd-label">Airline / hotel chain</label>
              <input
                className="gd-input"
                value={airline}
                onChange={(e) => setAirline(e.target.value)}
                placeholder="IndiGo, Marriott…"
                data-testid="fulfil-airline"
              />
            </div>
            <div>
              <label className="gd-label">Valid until</label>
              <input
                type="date"
                className="gd-input"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                data-testid="fulfil-valid-until"
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="gd-label">Send via</label>
              <select
                className="gd-input"
                value={deliveryStatus}
                onChange={(e) => setDeliveryStatus(e.target.value)}
                data-testid="fulfil-delivery"
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="gd-label">Internal notes</label>
              <textarea
                className="gd-input"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Visible only to ops — e.g. fare class, supplier ref, customer special request"
                data-testid="fulfil-notes"
                style={{ resize: "vertical" }}
              />
            </div>
          </div>

          {msg && (
            <div
              style={{
                marginTop: 14,
                padding: "8px 12px",
                background: "var(--danger-soft)",
                color: "#991b1b",
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              {msg}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
            <button
              className="gd-btn-primary"
              style={{ flex: 1, minWidth: 180 }}
              disabled={busy}
              onClick={() => save("send")}
              data-testid="fulfil-send-btn"
            >
              Send to customer
            </button>
            <button
              className="gd-btn-ghost"
              disabled={busy}
              onClick={() => save("save")}
              data-testid="fulfil-save-btn"
            >
              Save
            </button>
            <button
              className="gd-btn-ghost"
              disabled={busy}
              onClick={() => save("reissue")}
              data-testid="fulfil-reissue-btn"
            >
              Mark reissue
            </button>
            <button
              className="gd-btn-ghost"
              style={{ color: "#991b1b", borderColor: "#fecaca" }}
              disabled={busy}
              onClick={() => save("refund")}
              data-testid="fulfil-refund-btn"
            >
              Trigger refund
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Panel({ token, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ today: 0, in_fulfilment: 0, delivered: 0, needs_action: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [selected, setSelected] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const [o, s] = await Promise.all([
        axios.get(`${API}/admin/orders`, auth),
        axios.get(`${API}/admin/stats`, auth),
      ]);
      setOrders(o.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (activeTab === "all") return orders;
    if (activeTab === "paid") return orders.filter((o) => o.payment_status === "paid" && o.fulfilment_status === "in_fulfilment");
    if (activeTab === "in_fulfilment") return orders.filter((o) => o.fulfilment_status === "in_fulfilment");
    if (activeTab === "pnr_generated") return orders.filter((o) => o.fulfilment_status === "pnr_generated");
    if (activeTab === "sent") return orders.filter((o) => o.fulfilment_status === "sent");
    if (activeTab === "reissue_requested") return orders.filter((o) => o.fulfilment_status === "reissue_requested");
    return orders;
  }, [orders, activeTab]);

  const onUpdated = (updated) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setSelected(updated);
    load();
  };

  const visaCountry = (code) => {
    // derive simple country hint from destination airport code
    const map = {
      LHR: "UK", CDG: "France", FRA: "Germany", AMS: "Netherlands",
      DXB: "UAE", YYZ: "Canada", SYD: "Australia", JFK: "USA",
      SIN: "Singapore", BKK: "Thailand", NRT: "Japan", KUL: "Malaysia",
      ZRH: "Switzerland", VIE: "Austria", BRU: "Belgium", FCO: "Italy",
      MAD: "Spain", BCN: "Spain", MUC: "Germany", ARN: "Sweden",
      OSL: "Norway", CPH: "Denmark", HEL: "Finland", WAW: "Poland",
      PRG: "Czechia", BUD: "Hungary", ATH: "Greece", LIS: "Portugal",
      DUB: "Ireland", MEL: "Australia",
    };
    return map[code] || "—";
  };

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      <Navbar variant="admin" />
      <main className="gd-page" style={{ padding: "32px 24px 80px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 }}>Orders</h1>
            <p style={{ color: "var(--text-muted)", margin: "4px 0 0", fontSize: 14 }}>
              Total {stats.total ?? orders.length} orders · live from MongoDB
            </p>
          </div>
          <button
            className="gd-btn-ghost"
            onClick={onLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {/* Stats row */}
        <div
          className="gd-grid-3"
          style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 32, marginBottom: 32 }}
        >
          <div data-testid="stat-today">
            <div className="gd-stat-value" style={{ color: "var(--primary)" }}>{stats.today}</div>
            <div className="gd-stat-label">Today's orders</div>
          </div>
          <div data-testid="stat-in-fulfilment">
            <div className="gd-stat-value" style={{ color: "#d97706" }}>{stats.in_fulfilment}</div>
            <div className="gd-stat-label">In fulfilment</div>
          </div>
          <div data-testid="stat-delivered">
            <div className="gd-stat-value" style={{ color: "#15803d" }}>{stats.delivered}</div>
            <div className="gd-stat-label">Delivered</div>
          </div>
          <div data-testid="stat-needs-action">
            <div className="gd-stat-value" style={{ color: "#dc2626" }}>{stats.needs_action}</div>
            <div className="gd-stat-label">Needs action</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {[
            { id: "all", label: "All" },
            { id: "paid", label: "Paid" },
            { id: "in_fulfilment", label: "In fulfilment" },
            { id: "pnr_generated", label: "PNR generated" },
            { id: "sent", label: "Sent" },
            { id: "reissue_requested", label: "Reissue requested" },
          ].map((t) => (
            <button
              key={t.id}
              className={`gd-tab ${activeTab === t.id ? "active" : ""}`}
              onClick={() => setActiveTab(t.id)}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="gd-card" style={{ overflowX: "auto", padding: 0 }}>
          <table className="gd-table" data-testid="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Product</th>
                <th>Route / city</th>
                <th>Trip</th>
                <th>Validity</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Price</th>
                <th>Payment</th>
                <th>Fulfilment</th>
                <th>PNR / ref</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={12} style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                    No orders in this view.
                  </td>
                </tr>
              )}
              {!loading && filtered.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.order_code}`}>
                  <td style={{ fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>{o.order_code}</td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{productLabel(o)}</div>
                    {o.departure_date && (
                      <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                        {o.departure_date}
                        {o.return_date ? ` → ${o.return_date}` : ""}
                      </div>
                    )}
                    {o.check_in_date && (
                      <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                        {o.check_in_date}
                        {o.check_out_date ? ` → ${o.check_out_date}` : ""}
                      </div>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {o.flight_origin && o.flight_destination
                      ? `${o.flight_origin} → ${o.flight_destination}`
                      : o.hotel_cities || o.hotel_city || "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {o.product_snapshot?.requiresReturnDate
                      ? "Return"
                      : o.product_snapshot?.requiresFlight
                      ? "One-way"
                      : "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{validityLabel(o)}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{o.full_name}</div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <div>{o.email}</div>
                    <div style={{ color: "var(--text-muted)" }}>+91 {o.whatsapp}</div>
                  </td>
                  <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {formatPrice(o.amount || 0)}
                  </td>
                  <td><StatusBadge status={o.payment_status} /></td>
                  <td><StatusBadge status={o.fulfilment_status} /></td>
                  <td style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                    {o.pnr || "—"}
                    {o.airline && (
                      <div style={{ fontFamily: "inherit", fontSize: 11.5, color: "var(--text-muted)" }}>
                        {o.airline}
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      className="gd-btn-ghost"
                      onClick={() => setSelected(o)}
                      data-testid={`fulfil-btn-${o.order_code}`}
                    >
                      Fulfil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {selected && (
        <FulfilModal
          order={selected}
          token={token}
          onClose={() => setSelected(null)}
          onUpdated={onUpdated}
        />
      )}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [verifying, setVerifying] = useState(!!token);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    axios
      .get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setVerifying(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (verifying) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loader2 size={20} />
      </div>
    );
  }

  if (!token) return <Login onAuth={setToken} />;
  return <Panel token={token} onLogout={logout} />;
}
